import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import classNames from "classnames";
import ErrorOutlineOutlined from "@mui/icons-material/ErrorOutlineOutlined";

import EdgeLogo from "components/Design/EdgeLogo/EdgeLogo";

import { GetPlatformConfig } from "store/actions/PlatformConfig";

import { analytics } from "services/analytics";
import { joinWaitlistApi } from "services/waitlist";
import { s3ImagesBaseURL } from "util/APIBaseUrl";
import { buildAttribution, buildWaitlistContext } from "./attribution";
import FEATURE_TILES from "./featureTiles";

import "./Waitlist.scss";

// The page icons are served from the shared S3 bucket instead of being bundled.
// The bucket sends no CORS headers, so `<img>` is the only way to render them —
// `mask-image` recolouring and fetch-and-inline both need a CORS grant. Every
// colour is already baked into the file, so nothing has to be tinted in CSS.
const iconUrl = (name) => `${s3ImagesBaseURL}/${name}.svg`;

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const EMPTY_EMAIL_MESSAGE = "Please enter your email address";
const INVALID_EMAIL_MESSAGE = "Please enter a valid email address";
const GENERIC_ERROR_MESSAGE = "Something went wrong. Please try again.";
const EMAIL_INPUT_ID = "waitlist-email";
const EMAIL_ERROR_ID = "waitlist-email-error";

// The backend discards anything submitted less than two seconds after the form was
// rendered, and it now reads that window off the client-measured `elapsedMs` rather
// than its own clock. The submit button is held for the same window so a genuinely
// fast human can never trip that trap and be dropped without being told. Nobody types
// an email address in under two seconds, so it is invisible in normal use.
const MIN_FORM_ELAPSED_MS = 2000;

// `src/index.css` pins `body { overflow: hidden }` for the authenticated shell.
// This marketing page scrolls, so it opts the body out for as long as it is mounted.
const BODY_SCROLL_CLASS = "waitlist-page-open";

const validateEmail = (value) => {
  const trimmed = value.trim();
  if (!trimmed) {
    return EMPTY_EMAIL_MESSAGE;
  }
  if (!EMAIL_PATTERN.test(trimmed)) {
    return INVALID_EMAIL_MESSAGE;
  }
  return "";
};

function Waitlist() {
  const dispatch = useDispatch();
  // The same value `SegmentScript` keys off. Until it lands in redux the Segment
  // snippet has not been injected and `window.analytics` does not exist.
  const environment = useSelector((state) => state.PlatformConfig?.platformConfig?.payload?.ENVIRONMENT);

  const [email, setEmail] = useState("");
  const [website, setWebsite] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [hasMinTimeElapsed, setHasMinTimeElapsed] = useState(false);

  // Captured once, on first render. Only the elapsed time between this and the submit
  // is ever sent — never the absolute timestamp, which the backend would have had to
  // read against its own clock, so a visitor whose device clock ran a few minutes fast
  // silently tripped the time trap and lost the lead.
  const renderedAtRef = useRef(Date.now());
  // Synchronous double-submit guard. `isSubmitting` only disables the button after
  // the next render, which a fast double click can beat.
  const inFlightRef = useRef(false);
  // The page event is fired from an effect keyed on the environment, so this keeps
  // it to exactly one call no matter how many times that effect re-runs.
  const pageTrackedRef = useRef(false);
  // The success state replaces the whole form, so the submit button that had focus is
  // unmounted and focus would fall back to `<body>`. Focus is moved here instead.
  const successCardRef = useRef(null);
  // The spotlight is painted by writing two CSS custom properties straight onto this
  // node. Holding the pointer position in React state instead would re-render the
  // whole page — all twelve tiles included — on every single mouse move.
  const spotlightRef = useRef(null);
  // Coalesces a burst of `mousemove` events down to one paint per animation frame.
  const spotlightFrameRef = useRef(null);
  const spotlightPositionRef = useRef({ x: 0, y: 0 });

  const attribution = useMemo(() => buildAttribution(), []);

  useEffect(() => {
    document.body.classList.add(BODY_SCROLL_CLASS);
    return () => {
      document.body.classList.remove(BODY_SCROLL_CLASS);
    };
  }, []);

  useEffect(() => {
    const remainingMs = MIN_FORM_ELAPSED_MS - (Date.now() - renderedAtRef.current);
    if (remainingMs <= 0) {
      setHasMinTimeElapsed(true);
      return undefined;
    }
    const timer = setTimeout(() => setHasMinTimeElapsed(true), remainingMs);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    // `SegmentScript` only injects the snippet once `platformConfig.payload.ENVIRONMENT`
    // lands in redux, and that only happens when a screen asks for it. Without this
    // dispatch a visitor who lands straight on /signup gets no analytics at all.
    dispatch(GetPlatformConfig({}));
    // Mount-only on purpose.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Deliberately NOT a bare mount effect. On a cold load `window.analytics` does
    // not exist until `SegmentScript` has injected the snippet, which only happens
    // after the dispatch above resolves and `ENVIRONMENT` lands in redux — a page
    // call made before that is silently swallowed by the provider's optional
    // chaining. Waiting on `environment` puts the call after the snippet exists,
    // and the ref keeps it to exactly one event for the life of the page.
    if (!environment || pageTrackedRef.current) {
      return;
    }
    pageTrackedRef.current = true;
    analytics.page("Waitlist", attribution);
  }, [environment, attribution]);

  useEffect(() => {
    if (isSubmitted) {
      successCardRef.current?.focus();
    }
  }, [isSubmitted]);

  useEffect(() => {
    const spotlight = spotlightRef.current;
    if (!spotlight) {
      return undefined;
    }

    const paint = () => {
      spotlightFrameRef.current = null;
      const { x, y } = spotlightPositionRef.current;
      spotlight.style.setProperty("--wl-spot-x", `${x}px`);
      spotlight.style.setProperty("--wl-spot-y", `${y}px`);
    };

    const handleMouseMove = (event) => {
      // The overlay is `position: fixed`, so the client coordinates are already what
      // the gradient wants — there is no scroll offset to add.
      spotlightPositionRef.current = { x: event.clientX, y: event.clientY };
      // A frame is already queued; it will read the position above when it runs.
      if (spotlightFrameRef.current !== null) {
        return;
      }
      spotlightFrameRef.current = window.requestAnimationFrame(paint);
    };

    window.addEventListener("mousemove", handleMouseMove);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      if (spotlightFrameRef.current !== null) {
        window.cancelAnimationFrame(spotlightFrameRef.current);
        spotlightFrameRef.current = null;
      }
    };
  }, []);

  const handleEmailChange = useCallback(
    (event) => {
      const { value } = event.target;
      setEmail(value);
      // Only re-validate once the field has already errored, so the message clears
      // as the visitor fixes it. Never validate on a first blur before any submit.
      if (hasAttemptedSubmit) {
        setErrorMessage(validateEmail(value));
      }
    },
    [hasAttemptedSubmit]
  );

  const handleSubmit = useCallback(
    async (event) => {
      event.preventDefault();

      if (inFlightRef.current || isSubmitted) {
        return;
      }

      setHasAttemptedSubmit(true);
      // The submit button is now held disabled while the field is empty, so the
      // empty-email branch of `validateEmail` is unreachable from the UI. It stays
      // as defence in depth: a form can still be submitted programmatically.
      const validationMessage = validateEmail(email);
      if (validationMessage) {
        setErrorMessage(validationMessage);
        return;
      }

      inFlightRef.current = true;
      setErrorMessage("");
      setIsSubmitting(true);
      analytics.track("Waitlist Submit Attempted", attribution);

      try {
        const response = await joinWaitlistApi({
          email: email.trim(),
          website,
          elapsedMs: Date.now() - renderedAtRef.current,
          context: buildWaitlistContext(attribution),
        });

        if (response?.ok) {
          setIsSubmitted(true);
          analytics.track("Waitlist Signup Succeeded", attribution);
          return;
        }

        const serverMessage = response?.error?.[0]?.msg;
        setErrorMessage(serverMessage || GENERIC_ERROR_MESSAGE);
        analytics.track("Waitlist Signup Failed", {
          reason: serverMessage || "request_failed",
          status: response?.status,
          ...attribution,
        });
      } catch (error) {
        setErrorMessage(GENERIC_ERROR_MESSAGE);
        analytics.track("Waitlist Signup Failed", {
          reason: "network_error",
          status: undefined,
          ...attribution,
        });
      } finally {
        inFlightRef.current = false;
        setIsSubmitting(false);
      }
    },
    [attribution, email, isSubmitted, website]
  );

  const hasError = Boolean(errorMessage);

  return (
    <div className="waitlist-page">
      <span className="waitlist-page__glow waitlist-page__glow--top-right" aria-hidden="true" />
      <span className="waitlist-page__glow waitlist-page__glow--center-left" aria-hidden="true" />
      <span className="waitlist-page__glow waitlist-page__glow--mobile" aria-hidden="true" />
      <span className="waitlist-page__spotlight" ref={spotlightRef} aria-hidden="true" />

      <div className="waitlist-page__content">
        <header className="waitlist-header">
          <EdgeLogo size="lg" state="white" />
        </header>

        <main className="waitlist-body">
          <section className="waitlist-hero">
            <div className="waitlist-hero__text">
              <p className="waitlist-hero__eyebrow">EDGE TALENT NETWORK</p>
              <h1 className="waitlist-hero__headline">Join the waitlist</h1>
            </div>

            <div
              className={classNames("waitlist-card", { "waitlist-card--success": isSubmitted })}
              ref={successCardRef}
              role={isSubmitted ? "status" : undefined}
              tabIndex={isSubmitted ? -1 : undefined}
            >
              {isSubmitted ? (
                <>
                  <img
                    className="waitlist-card__success-icon"
                    src={iconUrl("check-circle-filled")}
                    alt=""
                    aria-hidden="true"
                    decoding="async"
                    width={32}
                    height={32}
                  />
                  <div className="waitlist-card__success-text">
                    <p className="waitlist-card__success-title">You’re on the list</p>
                    <p className="waitlist-card__success-subtitle">Stay tuned, we’ll email you an invite</p>
                  </div>
                </>
              ) : (
                <>
                  <form className="waitlist-form" onSubmit={handleSubmit} noValidate>
                    <div className="waitlist-form__row">
                      <div className={classNames("waitlist-form__field", { "waitlist-form__field--error": hasError })}>
                        <img
                          className="waitlist-form__field-icon"
                          src={iconUrl("mail")}
                          alt=""
                          aria-hidden="true"
                          decoding="async"
                          width={16}
                          height={16}
                        />
                        {/* The label IS associated, via htmlFor -> the input's id. Airbnb's
                          jsx-a11y preset sets `assert: "both"`, which additionally demands the
                          control be nested inside the label; nesting it would add a wrapper to
                          the flex row and change the layout, and `frontend/` turns this rule off
                          outright. */}
                        {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                        <label className="waitlist-sr-only" htmlFor={EMAIL_INPUT_ID}>
                          Email address
                        </label>
                        <input
                          id={EMAIL_INPUT_ID}
                          className="waitlist-form__input"
                          type="email"
                          name="email"
                          value={email}
                          onChange={handleEmailChange}
                          placeholder="you@yourpractice.com"
                          autoComplete="email"
                          inputMode="email"
                          disabled={isSubmitting}
                          aria-invalid={hasError}
                          aria-describedby={hasError ? EMAIL_ERROR_ID : undefined}
                        />
                      </div>

                      <button
                        type="submit"
                        className="waitlist-form__submit"
                        disabled={isSubmitting || !hasMinTimeElapsed || !email.trim()}
                      >
                        <span className="waitlist-form__submit-label">Join the Waitlist</span>
                        {isSubmitting ? (
                          <span className="waitlist-form__spinner" aria-hidden="true" />
                        ) : (
                          <img
                            className="waitlist-form__submit-icon"
                            src={iconUrl("arrow-right")}
                            alt=""
                            aria-hidden="true"
                            decoding="async"
                            width={14}
                            height={14}
                          />
                        )}
                      </button>
                    </div>

                    {hasError ? (
                      <div className="waitlist-form__error" id={EMAIL_ERROR_ID} role="alert">
                        <ErrorOutlineOutlined className="waitlist-form__error-icon" fontSize="inherit" />
                        <span className="waitlist-form__error-text">{errorMessage}</span>
                      </div>
                    ) : null}

                    {/* Honeypot. Real visitors never see it; it is kept off-screen rather than
                      display:none because bots detect the latter. */}
                    <input
                      type="text"
                      name="website"
                      className="waitlist-form__honeypot"
                      value={website}
                      onChange={(event) => setWebsite(event.target.value)}
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      data-testid="waitlist-honeypot"
                    />
                  </form>

                  <div className="waitlist-card__reassurance">
                    <img
                      className="waitlist-card__reassurance-icon"
                      src={iconUrl("shield-check")}
                      alt=""
                      aria-hidden="true"
                      decoding="async"
                      width={14}
                      height={14}
                    />
                    <p className="waitlist-card__reassurance-text">
                      <span>Zero spam. No lock-in.</span> <span>No payment required until you hire.</span>
                    </p>
                  </div>
                </>
              )}
            </div>
          </section>

          <p className="waitlist-subcopy">
            A network of vetted profiles and an entire ecosystem meticulously designed to redefine the way you hire.
          </p>

          <section className="waitlist-features">
            {FEATURE_TILES.map(({ id, icon, title, description }) => (
              <article className="waitlist-feature" key={id}>
                <img
                  className="waitlist-feature__icon"
                  src={iconUrl(icon)}
                  alt=""
                  aria-hidden="true"
                  decoding="async"
                  loading="lazy"
                  width={24}
                  height={24}
                />
                <div className="waitlist-feature__text">
                  <h2 className="waitlist-feature__title">{title}</h2>
                  <div className="waitlist-feature__description-wrapper">
                    <p className="waitlist-feature__description">{description}</p>
                  </div>
                </div>
              </article>
            ))}
          </section>
        </main>

        <footer className="waitlist-footer">
          <p className="waitlist-footer__text">
            © 2026 Edge - All rights reserved<span className="waitlist-footer__period">.</span>
          </p>
        </footer>
      </div>
    </div>
  );
}

export default Waitlist;
